/* Product Tile Markup - You can play around with this to change the
information that appears on the Wishlisted Product tiles  */
const WISHLIST_SPINNER_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66" aria-hidden="true"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>';
const WISHLIST_BUTTON_SPINNER = `<span class="loading__spinner hidden" aria-hidden="true">${WISHLIST_SPINNER_SVG}</span>`;

class SwymWishlist extends HTMLElement {
  constructor() {
    super();
    this.elm = this;

    this.wishlistContainer = null;
    this.wishlistCountElements = null;
    this.cartDrawer = null;
    this.tabHeadings = null;
    this.renderTimeout = null;
    this._swatchHtmlCache = new Map();

    this.debouncedRenderWishlist = this.debounce(this.renderWishlist.bind(this), 300);

    this.productCardMarkup = `<div class="swym-wishlist-grid">
	  <div class="swym-wishlist-grid__items">
		  {{#products}}
		  <article id="SwymWishlist-Item-{{empi}}" class="swym-wishlist-card{{#showNotifySlot}} swym-wishlist-card--unavailable{{/showNotifySlot}}" data-variant-available="{{availableFlag}}" data-variant-id="{{epi}}" data-product-handle="{{productHandle}}">
			<div class="swym-wishlist-card__media">
			  <a href="{{du}}" class="swym-wishlist-card__image-link" tabindex="-1" aria-hidden="true"></a>
			  {{#iu}}<img alt="{{dt}}" class="swym-wishlist-card__image" src="{{iu}}" loading="eager" decoding="async" width="169" height="253">{{/iu}}
			  <button type="button" aria-label="{{removeLabel}}" data-variant-id="{{epi}}" data-product-id="{{empi}}" class="swym-delete-btn swym-wishlist-card__remove">
			    <svg class="swym-wishlist-card__remove-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			      <path d="M1 1L11 11M11 1L1 11" stroke="#1E1E1E" stroke-width="0.5"/>
              </svg>
              ${WISHLIST_BUTTON_SPINNER}
            </button>
          </div>
			<div class="swym-wishlist-card__info">
			  <a href="{{du}}" class="swym-wishlist-card__title">{{dt}}</a>
			  <p class="swym-wishlist-card__price"><span class="swym-product-final-price">{{formattedPrice}}</span></p>
			  {{{colorSwatchesHtml}}}
			  </div>
			{{#showAddToCart}}
			<div class="swym-wishlist-card__actions">
			  <button type="button" data-state-cart="{{#isInCart}} swym-added{{/isInCart}}" data-product-url="{{du}}" data-variant-id="{{epi}}" data-product-id="{{empi}}" class="swym-wishlist-card__add js-wishlist-add-to-cart">{{addToCartLabel}}${WISHLIST_BUTTON_SPINNER}</button>
			</div>
			{{/showAddToCart}}
			{{#showNotifySlot}}
			<div class="swym-wishlist-card__notify-slot">
			  <button type="button" id="{{restockButtonId}}" class="swym-wishlist-card__notify-anchor" data-variant-id="{{epi}}">{{notifyMeLabel}}</button>
			  <input type="hidden" class="swym-wishlist-card__variant-input" value="{{epi}}">
			</div>
			{{/showNotifySlot}}
		  </article>
		  {{/products}}
	  </div>
	</div>`;
  }

  debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  static getPersistedWishlistCount(swat) {
    const upi = swat?.persistedData?.upi;
    return Array.isArray(upi) ? upi.length : 0;
  }

  static isWishlistSubmitDisabled(button) {
    return (
      !button ||
      button.getAttribute('aria-disabled') === 'true' ||
      button.classList.contains('disabled')
    );
  }

  static setWishlistActionLoading(button, isLoading) {
    if (!button) {
      return;
    }

    const spinner = button.querySelector('.loading__spinner');

    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      spinner?.classList.remove('hidden');
      return;
    }

    button.classList.remove('is-loading');
    spinner?.classList.add('hidden');
    button.removeAttribute('aria-disabled');
  }

  static setWishlistCardLoading(card, isLoading) {
    card?.classList.toggle('swym-wishlist-card--loading', Boolean(isLoading));
  }

  static shouldProcessWishlistActivation(target) {
    const now = Date.now();

    if (
      SwymWishlist._lastActivationTarget === target &&
      now - SwymWishlist._lastActivationTime < 400
    ) {
      return false;
    }

    SwymWishlist._lastActivationTarget = target;
    SwymWishlist._lastActivationTime = now;
    return true;
  }

  static handleWishlistActivation(event) {
    if (event.type === 'pointerup' && event.pointerType === 'mouse') {
      return;
    }

    const wishlistElement = document.querySelector('swym-wishlist');

    if (!wishlistElement) {
      return;
    }

    const submitButton = event.target.closest('.custom-quick-add-overlay--wishlist .cqa-add-btn[name="add"]');

    if (submitButton && !SwymWishlist.isWishlistSubmitDisabled(submitButton)) {
      if (!SwymWishlist.shouldProcessWishlistActivation(submitButton)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      wishlistElement.submitWishlistQuickAdd(submitButton);
      return;
    }

    const button = event.target.closest('#cart-wishlist .js-wishlist-add-to-cart');

    if (!button || button.hidden || button.getAttribute('aria-disabled') === 'true') {
      return;
    }

    // Mobile fires pointerup then click for the same tap. Only handle click for the
    // card ATC button so async product fetch cannot race into a direct add.
    if (event.type === 'pointerup') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    if (button.dataset.processingAdd === 'true') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    if (!SwymWishlist.shouldProcessWishlistActivation(button)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    wishlistElement.onAddToCartClick({ preventDefault: () => {}, currentTarget: button });
  }

  static bindWishlistActivationListeners() {
    if (SwymWishlist._wishlistActivationBound) {
      return;
    }

    SwymWishlist._wishlistActivationBound = true;
    SwymWishlist._lastActivationTarget = null;
    SwymWishlist._lastActivationTime = 0;

    document.addEventListener('click', SwymWishlist.handleWishlistActivation, true);
    document.addEventListener('pointerup', SwymWishlist.handleWishlistActivation, true);
  }

  static removeNotifyNode(node) {
    if (!node) {
      return;
    }

    const portal = node.closest('.nm-portal');

    if (portal && portal !== node) {
      portal.remove();
      return;
    }

    node.remove();
  }

  static dedupeNotifyButtonsInCard(card, options = {}) {
    if (!card) {
      return;
    }

    const notifySlotSelector = Object.prototype.hasOwnProperty.call(options, 'notifySlotSelector')
      ? options.notifySlotSelector
      : '.swym-wishlist-card__notify-slot';
    const placeholderAnchorSelector = Object.prototype.hasOwnProperty.call(options, 'placeholderAnchorSelector')
      ? options.placeholderAnchorSelector
      : '.swym-wishlist-card__notify-anchor:not(.restock-alerts-notify-button)';
    const notifyButtons = Array.from(card.querySelectorAll('.restock-alerts-notify-button'));

    if (options.removeAllNotify) {
      notifyButtons.forEach((button) => {
        SwymWishlist.removeNotifyNode(button);
      });
      card.querySelectorAll('.nm-portal').forEach((portal) => portal.remove());
      return;
    }

    notifyButtons.slice(1).forEach((button) => {
      SwymWishlist.removeNotifyNode(button);
    });

    const placeholderAnchor = placeholderAnchorSelector ? card.querySelector(placeholderAnchorSelector) : null;
    const primaryNotify = card.querySelector('.restock-alerts-notify-button');

    if (primaryNotify && placeholderAnchor && primaryNotify !== placeholderAnchor) {
      placeholderAnchor.remove();
    }

    const notifySlot = notifySlotSelector ? card.querySelector(notifySlotSelector) : null;

    if (notifySlot && primaryNotify) {
      const primaryRoot = primaryNotify.closest('.nm-portal') || primaryNotify;

      if (!notifySlot.contains(primaryRoot)) {
        notifySlot.appendChild(primaryRoot);
      }
    }
  }

  static getRecommendationSlider() {
    const slider = document.querySelector('.cart_drawer_wrap .cart_drawer_footer_slider');

    if (!slider || !slider.closest('#cart-products')) {
      return null;
    }

    return slider;
  }

  static getRecommendationProductHandles(slider) {
    const handles = new Set();

    slider.querySelectorAll('.card-wrapper .card__heading a[href*="/products/"]').forEach((link) => {
      const match = link.getAttribute('href')?.match(/\/products\/([^/?#]+)/);

      if (match) {
        handles.add(match[1]);
      }
    });

    return handles;
  }

  static restockIdMatchesHandle(restockId, handle) {
    if (!restockId || !handle) {
      return false;
    }

    return restockId === `restock-${handle}` || restockId.startsWith(`restock-${handle}-`);
  }

  static restockIdMatchesAnyHandle(restockId, handles) {
    for (const handle of handles) {
      if (SwymWishlist.restockIdMatchesHandle(restockId, handle)) {
        return true;
      }
    }

    return false;
  }

  static cleanupOrphanedRecommendationNotifyPortals(slider) {
    if (!slider) {
      return;
    }

    const liveCards = Array.from(slider.querySelectorAll('.card-wrapper'));
    const liveHandles = SwymWishlist.getRecommendationProductHandles(slider);
    const cartDrawer = document.querySelector('.cart_drawer_wrap');

    const isInsideLiveCard = (node) => liveCards.some((card) => card.contains(node));

    slider.querySelectorAll('.restock-alerts-notify-button, .nm-portal').forEach((node) => {
      if (!isInsideLiveCard(node)) {
        SwymWishlist.removeNotifyNode(node);
      }
    });

    if (!cartDrawer) {
      return;
    }

    cartDrawer.querySelectorAll('.restock-alerts-notify-button, .nm-portal').forEach((node) => {
      if (isInsideLiveCard(node)) {
        return;
      }

      const restockAnchor = node.matches('[id^="restock-"]')
        ? node
        : node.querySelector('[id^="restock-"]');
      const restockId = restockAnchor?.id || '';

      if (SwymWishlist.restockIdMatchesAnyHandle(restockId, liveHandles)) {
        SwymWishlist.removeNotifyNode(node);
      }
    });

    document.querySelectorAll('body .nm-portal, body .restock-alerts-notify-button').forEach((node) => {
      if (cartDrawer.contains(node) && isInsideLiveCard(node)) {
        return;
      }

      const restockAnchor = node.matches('[id^="restock-"]')
        ? node
        : node.querySelector('[id^="restock-"]');
      const restockId = restockAnchor?.id || '';

      if (SwymWishlist.restockIdMatchesAnyHandle(restockId, liveHandles)) {
        SwymWishlist.removeNotifyNode(node);
      }
    });
  }

  static syncCartDrawerRecommendationNotify() {
    const slider = SwymWishlist.getRecommendationSlider();

    if (!slider) {
      return;
    }

    slider.querySelectorAll('.card-wrapper').forEach((card) => {
      SwymWishlist.dedupeNotifyButtonsInCard(card, {
        notifySlotSelector: null,
        placeholderAnchorSelector: null,
        removeAllNotify: true,
      });
    });

    slider.querySelectorAll('.restock-alerts-notify-button, .nm-portal').forEach((node) => {
      SwymWishlist.removeNotifyNode(node);
    });

    SwymWishlist.cleanupOrphanedRecommendationNotifyPortals(slider);
  }

  static initCartDrawerRecommendationNotify() {
    const runPass = () => {
      SwymWishlist.syncCartDrawerRecommendationNotify();
    };

    runPass();

    if (SwymWishlist._recommendationNotifyInitDelays) {
      SwymWishlist._recommendationNotifyInitDelays.forEach((timer) => clearTimeout(timer));
    }

    SwymWishlist._recommendationNotifyInitDelays = [200, 500, 1000].map((delay) => setTimeout(runPass, delay));
  }

  static setupRecommendationNotifyDedupe() {
    const slider = SwymWishlist.getRecommendationSlider();

    if (!slider) {
      if (SwymWishlist._recommendationNotifyDedupeObserver) {
        SwymWishlist._recommendationNotifyDedupeObserver.disconnect();
        SwymWishlist._recommendationNotifyDedupeObserver = null;
        SwymWishlist._recommendationNotifyObservedSlider = null;
      }

      return;
    }

    if (!SwymWishlist._debouncedDedupeRecommendationNotify) {
      SwymWishlist._debouncedDedupeRecommendationNotify = SwymWishlist._createDebouncedRecommendationNotifyDedupe();
    }

    if (
      SwymWishlist._recommendationNotifyObservedSlider === slider &&
      SwymWishlist._recommendationNotifyDedupeObserver
    ) {
      return;
    }

    if (SwymWishlist._recommendationNotifyDedupeObserver) {
      SwymWishlist._recommendationNotifyDedupeObserver.disconnect();
    }

    SwymWishlist._recommendationNotifyObservedSlider = slider;
    SwymWishlist._recommendationNotifyDedupeObserver = new MutationObserver(() => {
      SwymWishlist._debouncedDedupeRecommendationNotify();
    });

    SwymWishlist._recommendationNotifyDedupeObserver.observe(slider, {
      childList: true,
      subtree: true,
    });
  }

  static _createDebouncedRecommendationNotifyDedupe() {
    let timer;

    return () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        SwymWishlist.syncCartDrawerRecommendationNotify();
      }, 100);
    };
  }

  connectedCallback() {
    this.boundRenderWishlist = this.debouncedRenderWishlist;

    if (window.SwymCallbacks && typeof window.SwymCallbacks.push === 'function') {
      window.SwymCallbacks.push(this.boundRenderWishlist);
    }

    this.wishlistContainer = document.getElementById('wishlist-items-container');
    this.addToCartLabel = this.dataset.addLabel || 'Add to cart';
    this.removeLabel = this.dataset.removeLabel || 'Remove';
    this.notifyMeLabel = this.dataset.notifyLabel || 'COMING SOON';
    this.notifyMeIcon = this.dataset.notifyIcon || '';
    this.handleWishlistClick = this.handleWishlistClick.bind(this);
    this.addEventListener('click', this.handleWishlistClick, true);
    SwymWishlist.bindWishlistActivationListeners();

    this.handleWishlistNotifyInit = this.handleWishlistNotifyInit.bind(this);
    document.addEventListener('wishlist:rendered', this.handleWishlistNotifyInit);
    document.addEventListener('cart-drawer:wishlist-tab-active', this.handleWishlistNotifyInit);

    requestIdleCallback(
      () => {
        this.initCartDrawerTabs();
        this.setupNotifyButtonDedupe();
        SwymWishlist.setupRecommendationNotifyDedupe();

        if (window._swat) {
          const count = SwymWishlist.getPersistedWishlistCount(window._swat);
          if (!this.wishlistCountElements) {
            this.wishlistCountElements = document.querySelectorAll('.js-wishlist-count');
          }
          this.wishlistCountElements.forEach((el) => {
            el.textContent = count;
          });
        }
      },
      { timeout: 500 }
    );
  }

  syncWishlistCardActions() {
    const container = this.wishlistContainer || document.getElementById('wishlist-items-container');

    if (!container || !container.closest('#cart-wishlist')) {
      return;
    }

    container.querySelectorAll('.swym-wishlist-card').forEach((card) => {
      const isAvailable = card.dataset.variantAvailable !== 'false';
      const addButton = card.querySelector('.swym-wishlist-card__add');

      if (isAvailable) {
        SwymWishlist.dedupeNotifyButtonsInCard(card, { removeAllNotify: true });

        if (addButton) {
          addButton.hidden = false;
          this.ensureActionsWrapper(card);
        }

        card
          .querySelectorAll(':scope > custom-quick-add-overlay.custom-quick-add-overlay--wishlist')
          .forEach((overlay) => overlay.remove());

        return;
      }

      if (addButton) {
        addButton.remove();
      }

      SwymWishlist.dedupeNotifyButtonsInCard(card);

      const notifySlot = card.querySelector('.swym-wishlist-card__notify-slot');
      if (notifySlot) {
        const notifyButtons = notifySlot.querySelectorAll(
          '.swym-wishlist-card__notify-anchor, .restock-alerts-notify-button'
        );
        notifyButtons.forEach((btn) => {
          if (typeof window.applyComingSoonButtonLabel === 'function') {
            window.applyComingSoonButtonLabel(btn, this.notifyMeLabel, this.notifyMeIcon);
          } else if (btn.textContent !== this.notifyMeLabel) {
            btn.textContent = this.notifyMeLabel;
          }
        });
      }
    });
  }

  ensureVariantProductUrl(url, variantId) {
    if (!url || !variantId) {
      return url;
    }

    try {
      const isAbsolute = url.startsWith('http');
      const parsed = new URL(url, window.location.origin);

      parsed.searchParams.set('variant', String(variantId));

      if (isAbsolute) {
        return parsed.toString();
      }

      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (error) {
      const cleanUrl = url.split('?')[0].split('#')[0];
      return `${cleanUrl}?variant=${variantId}`;
    }
  }

  nudgeCartDrawerNotifyAnchors() {
    const container = this.wishlistContainer || document.getElementById('wishlist-items-container');

    if (!container) {
      return;
    }

    container.querySelectorAll('[data-variant-available="false"] .swym-wishlist-card__notify-anchor').forEach((anchor) => {
      const card = anchor.closest('.swym-wishlist-card');

      if (!card || card.querySelector('.restock-alerts-notify-button')) {
        return;
      }

      const replacement = anchor.cloneNode(true);
      anchor.replaceWith(replacement);
    });
  }

  initCartDrawerNotifyButtons() {
    const wishlistTab = document.getElementById('cart-wishlist');

    if (!wishlistTab || wishlistTab.classList.contains('hidden')) {
      return;
    }

    const runPass = () => {
      this.nudgeCartDrawerNotifyAnchors();
      this.syncWishlistCardActions();
    };

    runPass();

    if (this._notifyInitDelays) {
      this._notifyInitDelays.forEach((timer) => clearTimeout(timer));
    }

    this._notifyInitDelays = [200, 500, 1000].map((delay) => setTimeout(runPass, delay));
  }

  handleWishlistNotifyInit() {
    this.initCartDrawerNotifyButtons();
    this.initWishlistSizeSelectors();
  }

  setupNotifyButtonDedupe() {
    this.wishlistContainer = this.wishlistContainer || document.getElementById('wishlist-items-container');

    if (!this.wishlistContainer || !this.wishlistContainer.closest('#cart-wishlist')) {
      return;
    }

    if (!this.debouncedDedupeNotifyButtons) {
      this.debouncedDedupeNotifyButtons = this.debounce(() => {
        this.syncWishlistCardActions();
      }, 100);
    }

    if (this._notifyDedupeObserver) {
      return;
    }

    this._notifyDedupeObserver = new MutationObserver(() => {
      this.debouncedDedupeNotifyButtons();
    });

    this._notifyDedupeObserver.observe(this.wishlistContainer, {
      childList: true,
      subtree: true,
    });
  }

  initCartDrawerTabs() {
    const cartWishlist = document.querySelector('#cart-wishlist');
    if (cartWishlist && !cartWishlist.classList.contains('hidden') && window._swat) {
      requestIdleCallback(
        () => {
          window.swymRenderWishlist(window._swat);
        },
        { timeout: 300 }
      );
    }
  }

  handleWishlistClick(event) {
    if (event.target.closest('.custom-quick-add-overlay--wishlist')) {
      return;
    }

    const removeBtn = event.target.closest('.swym-delete-btn');
    if (removeBtn) {
      event.preventDefault();
      this.onRemoveBtnClick({ preventDefault: () => {}, currentTarget: removeBtn });
    }
  }

  static get SIZE_OPTION_NAMES() {
    return ['size', 'talla', 'tallas', 'taille', 'variant'];
  }

  static get COLOR_OPTION_NAMES() {
    return ['color', 'colour', 'coloris', 'couleur'];
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text ?? '';
    return div.innerHTML;
  }

  getSizeOptionIndex(product) {
    if (!product?.options?.length) {
      return -1;
    }

    return product.options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option?.name || '';
      return SwymWishlist.SIZE_OPTION_NAMES.includes(String(name).toLowerCase());
    });
  }

  getEffectiveSizeOptionIndex(product) {
    const namedSizeIndex = this.getSizeOptionIndex(product);

    if (namedSizeIndex !== -1) {
      return namedSizeIndex;
    }

    if (!product?.options?.length) {
      return -1;
    }

    for (let index = 0; index < product.options.length; index += 1) {
      if (this.getSizeValues(product, index).length > 1) {
        return index;
      }
    }

    return -1;
  }

  getSizeValues(product, sizeIndex) {
    if (!product?.options?.length || sizeIndex < 0) {
      return [];
    }

    const sizeOption = product.options[sizeIndex];

    if (sizeOption && typeof sizeOption === 'object' && Array.isArray(sizeOption.values) && sizeOption.values.length) {
      return sizeOption.values;
    }

    const values = [];
    const seen = new Set();

    product.variants?.forEach((variant) => {
      const value = variant[`option${sizeIndex + 1}`];

      if (value && !seen.has(value)) {
        seen.add(value);
        values.push(value);
      }
    });

    return values;
  }

  needsSizeSelection(product) {
    const sizeIndex = this.getEffectiveSizeOptionIndex(product);

    if (sizeIndex === -1) {
      return false;
    }

    return this.getSizeValues(product, sizeIndex).length > 1;
  }

  getProductHandleFromCard(card, button) {
    return (
      card?.dataset.productHandle ||
      this.getHandleFromUrl(button?.getAttribute('data-product-url')) ||
      this.getHandleFromUrl(card?.querySelector('.swym-wishlist-card__title')?.getAttribute('href'))
    );
  }

  getColorOptionIndex(product) {
    if (!product?.options?.length) {
      return -1;
    }

    return product.options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option?.name || '';
      return SwymWishlist.COLOR_OPTION_NAMES.includes(String(name).toLowerCase());
    });
  }

  getWishlistQuickAddOverlayId(productId, wishlistedVariantId) {
    return `cart-wishlist-${productId}-${wishlistedVariantId}`;
  }

  getWishlistQuickAddOverlay(card) {
    if (!card) {
      return null;
    }

    const addButton = card.querySelector('.js-wishlist-add-to-cart');
    const productId = addButton?.getAttribute('data-product-id');
    const variantId = card.dataset.variantId || addButton?.getAttribute('data-variant-id');

    if (!productId || !variantId) {
      return card.querySelector('custom-quick-add-overlay.custom-quick-add-overlay--wishlist');
    }

    const overlayId = `#CustomQuickAdd-${this.getWishlistQuickAddOverlayId(productId, variantId)}`;

    return (
      card.querySelector(overlayId) ||
      document.querySelector(overlayId) ||
      null
    );
  }

  isWishlistQuickAddOverlayOpen(card) {
    const overlay = this.getWishlistQuickAddOverlay(card);

    return Boolean(overlay && !overlay.classList.contains('hidden'));
  }

  bindWishlistQuickAddSubmit() {
    SwymWishlist.bindWishlistActivationListeners();
  }

  resolveWishlistOverlayVariantId(overlay) {
    if (!overlay) {
      return null;
    }

    const variantInput = overlay.querySelector('.cqa-variant-id');

    if (variantInput?.value) {
      return String(variantInput.value);
    }

    const checkedSize = overlay.querySelector('input[name^="cqa-size-"]:checked');

    if (!checkedSize) {
      return null;
    }

    const checkedColor = overlay.querySelector('input[name^="cqa-color-"]:checked');
    const selectedSize = checkedSize.value;
    const selectedColor = checkedColor?.value || '';
    let variantsData = overlay.variantsData;

    if (!Array.isArray(variantsData) || !variantsData.length) {
      try {
        const dataEl = overlay.querySelector('.cqa-variant-data');
        variantsData = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (error) {
        variantsData = [];
      }
    }

    const matchingVariant = variantsData.find((variant) => {
      const hasSize = variant.options.includes(selectedSize);
      const hasColor = !selectedColor || variant.options.includes(selectedColor);
      return hasSize && hasColor;
    });

    if (!matchingVariant?.id) {
      return null;
    }

    if (variantInput) {
      variantInput.value = matchingVariant.id;
    }

    return String(matchingVariant.id);
  }

  getSelectedWishlistOverlayVariantId(card) {
    const overlay = this.getWishlistQuickAddOverlay(card);

    if (!overlay || overlay.classList.contains('hidden')) {
      return null;
    }

    const hasSelectedSize = overlay.querySelector('input[name^="cqa-size-"]:checked');

    if (!hasSelectedSize) {
      return null;
    }

    return this.resolveWishlistOverlayVariantId(overlay);
  }

  submitWishlistQuickAdd(submitButton) {
    const overlay = submitButton.closest('custom-quick-add-overlay');
    const card =
      overlay?.originalParent?.closest?.('.swym-wishlist-card') ||
      overlay?.originalParent ||
      overlay?.closest('.swym-wishlist-card');
    const addButton = card?.querySelector('.js-wishlist-add-to-cart');
    const variantId = this.resolveWishlistOverlayVariantId(overlay);
    const productId = overlay?.dataset.productId || addButton?.getAttribute('data-product-id');

    if (!addButton || !variantId || SwymWishlist.isWishlistSubmitDisabled(submitButton)) {
      return;
    }

    this.closeWishlistQuickAddOverlays(card);
    this.addVariantToCart(addButton, variantId, productId);
  }

  initWishlistSizeSelectors() {
    SwymWishlist.bindWishlistActivationListeners();
  }

  prepareWishlistQuickAddOverlay(overlay) {
    if (!overlay) {
      return;
    }

    if (typeof overlay.initializeOverlay === 'function') {
      overlay.initializeOverlay();
    } else if (typeof overlay.updateVariant === 'function') {
      overlay.updateVariant();
    }
  }

  getWishlistedVariant(product, variantId) {
    if (!product?.variants?.length || !variantId) {
      return null;
    }

    return product.variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  getAvailableSizes(product, wishlistedVariantId) {
    const sizeIndex = this.getEffectiveSizeOptionIndex(product);

    if (sizeIndex === -1) {
      return [];
    }

    const wishlistedVariant = this.getWishlistedVariant(product, wishlistedVariantId);
    const sizeValues = this.getSizeValues(product, sizeIndex);
    const lowStockEnabled = this.dataset.lowStockEnabled === 'true';
    const lowStockThreshold = parseInt(this.dataset.lowStockThreshold, 10) || 0;
    const lowStockText = this.dataset.lowStockText || '';

    return sizeValues.map((sizeValue) => {
      const matchingVariant = product.variants.find((variant) => {
        if (variant[`option${sizeIndex + 1}`] !== sizeValue) {
          return false;
        }

        if (!wishlistedVariant) {
          return true;
        }

        for (let index = 0; index < product.options.length; index += 1) {
          if (index === sizeIndex) {
            continue;
          }

          const wishlistedOption = wishlistedVariant[`option${index + 1}`];

          if (!wishlistedOption) {
            continue;
          }

          if (variant[`option${index + 1}`] !== wishlistedOption) {
            return false;
          }
        }

        return true;
      });

      const available = Boolean(matchingVariant?.available);
      const inventoryQuantity = matchingVariant?.inventory_quantity ?? 0;
      const lowStock =
        lowStockEnabled && available && inventoryQuantity > 0 && inventoryQuantity <= lowStockThreshold;

      return {
        sizeValue,
        variantId: matchingVariant?.id || null,
        available,
        lowStock,
        lowStockText: lowStock ? lowStockText : '',
      };
    });
  }

  buildWishlistQuickAddSizeListHtml(product, wishlistedVariantId, overlayId) {
    const sizes = this.getAvailableSizes(product, wishlistedVariantId);

    return sizes
      .map((size) => {
        const disabledClass = !size.available ? ' is-disabled' : '';
        const availableAttr = size.available ? ' data-available="true"' : '';
        let stockHtml = '';

        if (size.lowStock && size.available) {
          stockHtml = `<span class="cqa-stock-warning">${this.escapeHtml(size.lowStockText)}</span>`;
        } else if (!size.available) {
          stockHtml = `<span class="cqa-envelope-icon" aria-hidden="true"></span>`;
        }

        return `<label class="cqa-size-item${disabledClass}">
          <input type="radio" name="cqa-size-${overlayId}" value="${this.escapeHtml(size.sizeValue)}" class="visually-hidden"${availableAttr}>
          <span class="cqa-size-name">${this.escapeHtml(size.sizeValue)}</span>
          ${stockHtml}
        </label>`;
      })
      .join('');
  }

  buildWishlistQuickAddColorHtml(product, wishlistedVariantId, overlayId) {
    const colorIndex = this.getColorOptionIndex(product);

    if (colorIndex === -1) {
      return '';
    }

    const wishlistedVariant = this.getWishlistedVariant(product, wishlistedVariantId);
    const colorValue = wishlistedVariant?.[`option${colorIndex + 1}`];

    if (!colorValue) {
      return '';
    }

    return `<div class="cqa-color-section">
      <input type="radio" name="cqa-color-${overlayId}" value="${this.escapeHtml(colorValue)}" class="visually-hidden" checked>
    </div>`;
  }

  buildWishlistQuickAddVariantDataJson(product) {
    const variants = (product.variants || []).map((variant) => {
      const options =
        Array.isArray(variant.options) && variant.options.length
          ? variant.options
          : [variant.option1, variant.option2, variant.option3].filter(Boolean);

      let featuredImage = null;

      if (variant.featured_image) {
        featuredImage = {
          src:
            typeof variant.featured_image === 'string'
              ? variant.featured_image
              : variant.featured_image.src || variant.featured_image.url || null,
        };
      }

      return {
        id: variant.id,
        options,
        available: variant.available,
        inventory_quantity: variant.inventory_quantity ?? 0,
        featured_image: featuredImage,
      };
    });

    return JSON.stringify(variants);
  }

  buildWishlistQuickAddOverlayHtml(product, wishlistedVariantId, productId) {
    const overlayId = this.getWishlistQuickAddOverlayId(productId, wishlistedVariantId);
    const addLabel = this.addToCartLabel || 'Add to cart';
    const lowStockAttrs =
      this.dataset.lowStockEnabled === 'true'
        ? ` data-low-stock-enabled="true" data-low-stock-threshold="${this.escapeHtml(this.dataset.lowStockThreshold || '')}" data-low-stock-text="${this.escapeHtml(this.dataset.lowStockText || '')}"`
        : '';

    return `<custom-quick-add-overlay class="custom-quick-add-overlay hidden custom-quick-add-overlay--wishlist" id="CustomQuickAdd-${overlayId}" data-product-id="${this.escapeHtml(String(productId || ''))}"${lowStockAttrs}>
      <div class="cqa-backdrop"></div>
      <div class="cqa-content">
        <div class="cqa-header">
          <button type="button" class="cqa-close" aria-label="Close">
            <svg viewBox="0 0 14 14" width="14" height="14"><path d="M13 1L1 13M1 1l12 12" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
          </button>
        </div>
        <div class="cqa-inner">
          <div class="cqa-options">
            <div class="cqa-size-list">${this.buildWishlistQuickAddSizeListHtml(product, wishlistedVariantId, overlayId)}</div>
            ${this.buildWishlistQuickAddColorHtml(product, wishlistedVariantId, overlayId)}
          </div>
          <div class="cqa-form-wrapper">
            <input type="hidden" class="cqa-variant-id" value="">
            <button type="button" name="add" class="button button--full-width button--primary cqa-add-btn">
              <span>${this.escapeHtml(addLabel)}</span>
            </button>
          </div>
        </div>
        <script type="application/json" class="cqa-variant-data">${this.buildWishlistQuickAddVariantDataJson(product)}</script>
      </div>
    </custom-quick-add-overlay>`;
  }

  ensureActionsWrapper(card) {
    const addButton = card?.querySelector('.js-wishlist-add-to-cart');

    if (!addButton) {
      return null;
    }

    let wrapper = addButton.closest('.swym-wishlist-card__actions');

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'swym-wishlist-card__actions';
      addButton.parentNode.insertBefore(wrapper, addButton);
      wrapper.appendChild(addButton);
    }

    return wrapper;
  }

  ensureWishlistQuickAddOverlay(card, product, wishlistedVariantId, productId) {
    if (!card) {
      return null;
    }

    const overlayId = this.getWishlistQuickAddOverlayId(productId, wishlistedVariantId);
    const overlaySelector = `#CustomQuickAdd-${overlayId}`;
    const actionsWrapper = this.ensureActionsWrapper(card);
    const mountTarget = actionsWrapper || card;

    card.querySelectorAll(':scope > custom-quick-add-overlay.custom-quick-add-overlay--wishlist').forEach((overlay) => {
      overlay.remove();
    });
    mountTarget.querySelector(overlaySelector)?.remove();
    document.querySelector(overlaySelector)?.remove();
    mountTarget.insertAdjacentHTML(
      'afterbegin',
      this.buildWishlistQuickAddOverlayHtml(product, wishlistedVariantId, productId)
    );

    return mountTarget.querySelector(overlaySelector);
  }

  restoreWishlistOverlaysFromBody() {
    if (window.innerWidth <= 768) {
      return;
    }

    document.querySelectorAll('body > custom-quick-add-overlay.custom-quick-add-overlay--wishlist').forEach((overlay) => {
      const card =
        overlay.originalParent?.closest?.('.swym-wishlist-card') ||
        document
          .querySelector(`#cart-wishlist custom-quick-add-overlay[id="${overlay.id}"]`)
          ?.closest('.swym-wishlist-card');

      const actionsWrapper = card ? this.ensureActionsWrapper(card) : overlay.originalParent;

      if (actionsWrapper && overlay.parentNode === document.body) {
        actionsWrapper.insertBefore(overlay, actionsWrapper.firstChild);
      }

      overlay.classList.add('hidden');
      card?.classList.remove('swym-wishlist-card--size-popup-open');
      card?.querySelector('.js-wishlist-add-to-cart')?.setAttribute('aria-expanded', 'false');
    });
  }

  selectFirstAvailableWishlistSize(overlay) {
    if (!overlay) {
      return;
    }

    const sizeInputs = overlay.querySelectorAll('input[name^="cqa-size-"]');

    if (!sizeInputs.length) {
      return;
    }

    const alreadyChecked = Array.from(sizeInputs).some((input) => input.checked);

    if (alreadyChecked) {
      return;
    }

    const firstAvailable =
      Array.from(sizeInputs).find((input) => input.dataset.available === 'true') || sizeInputs[0];

    if (firstAvailable) {
      firstAvailable.checked = true;
    }
  }

  openWishlistQuickAddOverlay(card, button, product, wishlistedVariantId, productId) {
    this.closeWishlistQuickAddOverlays(document.getElementById('cart-wishlist'));
    this.restoreWishlistOverlaysFromBody();

    const overlay = this.ensureWishlistQuickAddOverlay(card, product, wishlistedVariantId, productId);

    if (!overlay) {
      return;
    }

    const actionsWrapper = this.ensureActionsWrapper(card);

    if (actionsWrapper && overlay.parentNode !== actionsWrapper) {
      actionsWrapper.insertBefore(overlay, actionsWrapper.firstChild);
    }

    const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;

    if (isMobileViewport) {
      if (overlay.parentNode !== document.body) {
        overlay.originalParent = overlay.parentNode;
        overlay.originalNextSibling = overlay.nextSibling;
        document.body.appendChild(overlay);
      }
    } else if (overlay.parentNode === document.body && overlay.originalParent) {
      overlay.originalParent.insertBefore(overlay, overlay.originalNextSibling);
    }

    // Mark expanded before revealing so the same-tap mobile click cannot fall through.
    button.setAttribute('aria-expanded', 'true');
    card.classList.add('swym-wishlist-card--size-popup-open');
    overlay.dataset.openedAt = String(Date.now());
    overlay.classList.remove('hidden');
    this.prepareWishlistQuickAddOverlay(overlay);
    this.selectFirstAvailableWishlistSize(overlay);

    if (typeof overlay.updateVariant === 'function') {
      overlay.updateVariant();
    }
  }

  closeWishlistQuickAddOverlays(scope) {
    const closeOverlay = (overlay) => {
      if (window.innerWidth > 768 && overlay.parentNode === document.body && overlay.originalParent) {
        overlay.originalParent.insertBefore(overlay, overlay.originalNextSibling);
      }

      overlay.classList.add('hidden');
    };

    if (scope?.classList?.contains('swym-wishlist-card')) {
      const overlay = this.getWishlistQuickAddOverlay(scope);

      if (overlay) {
        closeOverlay(overlay);
      }

      scope.querySelectorAll('.js-wishlist-add-to-cart[aria-expanded="true"]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
      scope.classList.remove('swym-wishlist-card--size-popup-open');
    } else {
      const root = scope || document.getElementById('cart-wishlist');

      root?.querySelectorAll('custom-quick-add-overlay.custom-quick-add-overlay--wishlist:not(.hidden)').forEach(closeOverlay);
      document
        .querySelectorAll('body > custom-quick-add-overlay.custom-quick-add-overlay--wishlist:not(.hidden)')
        .forEach(closeOverlay);

      root?.querySelectorAll('.js-wishlist-add-to-cart[aria-expanded="true"]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      root?.querySelectorAll('.swym-wishlist-card--size-popup-open').forEach((entry) => {
        entry.classList.remove('swym-wishlist-card--size-popup-open');
      });
    }

  }

  getProductImageUrl(product) {
    let url =
      product.iu ||
      product.image ||
      product.im ||
      product.featured_image ||
      (product.productData && (product.productData.featured_image || product.productData.images?.[0])) ||
      '';

    if (product.product && !url) {
      url = product.product.iu || product.product.image || product.product.featured_image || '';
    }

    if (typeof url === 'object' && url !== null) {
      url = url.src || url.url || '';
    }

    return this.normalizeImageUrl(url);
  }

  normalizeImageUrl(url) {
    if (!url || typeof url !== 'string') {
      return '';
    }

    if (url.startsWith('//')) {
      url = `https:${url}`;
    }

    if (url.includes('cdn.shopify.com') && !url.includes('width=')) {
      url += `${url.includes('?') ? '&' : '?'}width=300`;
    }

    return url;
  }

  getHandleFromUrl(productUrl) {
    if (!productUrl || typeof productUrl !== 'string') {
      return null;
    }

    const trimmed = productUrl.trim();

    try {
      const path = trimmed.startsWith('http') ? new URL(trimmed).pathname : trimmed;
      const match = path.match(/\/products\/([^/?#]+)/);

      if (match) {
        return decodeURIComponent(match[1]);
      }
    } catch (error) {
      // fall through to bare-handle check
    }

    if (!trimmed.includes('/') && /^[a-z0-9-]+$/i.test(trimmed)) {
      return trimmed;
    }

    return null;
  }

  async searchHandleByTitle(title, productId) {
    if (!title) {
      return null;
    }

    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(
        `${root}search/suggest.json?q=${encodeURIComponent(title)}&resources[type]=product&resources[limit]=10`,
        { credentials: 'same-origin' }
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const results = data?.resources?.results?.products || [];

      if (productId) {
        const match = results.find((entry) => String(entry.id) === String(productId));

        if (match?.handle) {
          return match.handle;
        }
      }

      return results[0]?.handle || null;
    } catch (error) {
      return null;
    }
  }

  async resolveWishlistProductHandle(card, button, productId) {
    let handle = this.getProductHandleFromCard(card, button);

    if (!handle) {
      const productUrl =
        button?.getAttribute('data-product-url') ||
        card?.querySelector('.swym-wishlist-card__title')?.getAttribute('href') ||
        '';

      handle = this.getHandleFromUrl(productUrl);
    }

    if (!handle) {
      const title = card?.querySelector('.swym-wishlist-card__title')?.textContent?.trim();
      handle = await this.searchHandleByTitle(title, productId);
    }

    if (!handle && productId) {
      handle = await this.resolveProductHandle(null, null, productId);
    }

    return handle || null;
  }

  async resolveWishlistProduct(card, button, productId) {
    let handle = await this.resolveWishlistProductHandle(card, button, productId);

    if (!handle) {
      return null;
    }

    let product = await this.fetchProductJson(handle);

    if (product) {
      return product;
    }

    const title = card?.querySelector('.swym-wishlist-card__title')?.textContent?.trim();
    const fallbackHandle = await this.searchHandleByTitle(title, productId);

    if (fallbackHandle && fallbackHandle !== handle) {
      product = await this.fetchProductJson(fallbackHandle);
    }

    return product || null;
  }

  async fetchColorSwatchesHtml(handle, variantId) {
    if (!handle) {
      return '';
    }

    const cacheKey = `swatches:${handle}:${variantId || ''}`;

    if (this._swatchHtmlCache.has(cacheKey)) {
      return this._swatchHtmlCache.get(cacheKey);
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({ sections: 'wishlist-card-colors' });

      if (variantId) {
        params.set('variant', variantId);
      }

      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?${params.toString()}`, {
        credentials: 'same-origin',
      });

      if (!response.ok) {
        this._swatchHtmlCache.set(cacheKey, '');
            return '';
          }

      const data = await response.json();
      let html = data['wishlist-card-colors'] || '';

      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.shopify-section');

        if (section) {
          html = section.innerHTML.trim();
        }
      }

      this._swatchHtmlCache.set(cacheKey, html);
      return html;
    } catch (error) {
      this._swatchHtmlCache.set(cacheKey, '');
      return '';
    }
  }

  async fetchProductJson(handle) {
    if (!handle) {
      return null;
    }

    if (!this._productJsonCache) {
      this._productJsonCache = new Map();
    }

    const cacheKey = `product-json:${handle}`;

    if (this._productJsonCache.has(cacheKey)) {
      return this._productJsonCache.get(cacheKey);
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
      });

      if (!response.ok) {
        this._productJsonCache.set(cacheKey, null);
        return null;
      }

      const product = await response.json();
      this._productJsonCache.set(cacheKey, product);
      return product;
    } catch (error) {
      this._productJsonCache.set(cacheKey, null);
      return null;
    }
  }

  getVariantFromProductJson(productJson, variantId) {
    if (!productJson?.variants || variantId == null || variantId === '') {
      return null;
    }

    return productJson.variants.find((entry) => String(entry.id) === String(variantId)) || null;
  }

  getVariantImageUrl(productJson, variantId) {
    const variant = this.getVariantFromProductJson(productJson, variantId);

    if (!variant) {
      return '';
    }

    if (variant.featured_image) {
      const imageUrl =
        typeof variant.featured_image === 'string'
          ? variant.featured_image
          : variant.featured_image.src || variant.featured_image.url || '';

      return this.normalizeImageUrl(imageUrl);
    }

    if (variant.image_id && productJson.images?.length) {
      const image = productJson.images.find((entry) => String(entry.id) === String(variant.image_id));

      if (image) {
        return this.normalizeImageUrl(image.src || image.url || '');
      }
    }

    return '';
  }

  getVariantAvailabilityFromProduct(productJson, variantId) {
    if (!productJson) {
      return true;
    }

    const variant = this.getVariantFromProductJson(productJson, variantId);

    if (variant) {
      return Boolean(variant.available);
    }

    return Boolean(productJson.available);
  }

  async getVariantAvailability(handle, variantId) {
    const product = await this.fetchProductJson(handle);

    return this.getVariantAvailabilityFromProduct(product, variantId);
  }

  async fetchProductFromPredictiveSearch(product) {
    const root = window.Shopify?.routes?.root || '/';
    const cacheKey = `search:${product.empi}`;

    if (this._productCache?.has(cacheKey)) {
      return this._productCache.get(cacheKey);
    }

    const queries = new Set();
    if (product.dt) {
      queries.add(product.dt);
    }

    const handle = this.getHandleFromUrl(product.du || product.uri || product.product?.du);
    if (handle) {
      queries.add(handle.replace(/-/g, ' '));
    }

    for (const query of queries) {
      try {
        const searchUrl = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`;
        const response = await fetch(searchUrl);

        if (!response.ok) {
          continue;
        }

        const data = await response.json();
        const results = data?.resources?.results?.products || [];
        const match =
          results.find((entry) => String(entry.id) === String(product.empi)) ||
          results.find((entry) => entry.title?.toLowerCase() === product.dt?.toLowerCase());

        if (match) {
          const searchData = {
            title: match.title || '',
            image: match.image ? this.normalizeImageUrl(match.image) : '',
            handle: match.handle || '',
            url: match.url || '',
          };
          this._productCache.set(cacheKey, searchData);
          return searchData;
        }
      } catch (error) {
        continue;
      }
    }

    this._productCache.set(cacheKey, null);
    return null;
  }

  cleanProductTitle(title) {
    if (!title || typeof title !== 'string') {
      return title || '';
    }

    let cleaned = title.trim();

    if (cleaned.includes(' | ')) {
      cleaned = cleaned.split(' | ')[0];
    }
    if (cleaned.includes(' – ')) {
      cleaned = cleaned.split(' – ')[0];
    }
    if (cleaned.includes(' - ')) {
      const parts = cleaned.split(' - ');
      if (parts.length > 1 && /eseoese|store|shop/i.test(parts[parts.length - 1])) {
        parts.pop();
        cleaned = parts.join(' - ');
      }
    }

    return cleaned.trim();
  }

  getStorefrontCurrencySymbol() {
    return (
      this.dataset.currencySymbol ||
      window.eseoeseCollectionPrice?.currencySymbol ||
      window.Shopify?.currency?.symbol ||
      ''
    );
  }

  formatStorefrontPrice(cents) {
    if (cents == null || !Number.isFinite(Number(cents))) {
      return '';
    }

    const symbol = this.getStorefrontCurrencySymbol();
    const amount = (Number(cents) / 100).toFixed(2).replace('.', ',');
    return `${amount}${symbol}`;
  }

  resolveWishlistPriceCents(product) {
    if (product.priceCents != null && Number.isFinite(Number(product.priceCents))) {
      return Math.round(Number(product.priceCents));
    }

    const rawPr = product.pr;

    if (typeof rawPr === 'number' && Number.isFinite(rawPr)) {
      return Math.round(rawPr * 100);
    }

    if (typeof rawPr === 'string') {
      const parsed = parseFloat(rawPr.replace(',', '.'));

      if (Number.isFinite(parsed)) {
        return Math.round(parsed * 100);
      }
    }

    return null;
  }

  async enrichProductsFromShopify(products) {
    if (!products?.length) {
      return products;
    }

    if (!this._productCache) {
      this._productCache = new Map();
    }

    if (!this._productMissCache) {
      this._productMissCache = new Set();
    }

    return Promise.all(
      products.map(async (product) => {
        const enriched = { ...product };
        const searchData = await this.fetchProductFromPredictiveSearch(enriched);

        if (searchData?.url) {
          enriched.du = searchData.url.startsWith('http')
            ? searchData.url
            : `${window.location.origin}${searchData.url}`;
        }

        const handle = searchData?.handle || this.getHandleFromUrl(enriched.du);
        enriched.productHandle = handle || '';

        let imageUrl = '';
        let productJson = null;

        if (handle) {
          productJson = await this.fetchProductJson(handle);
          if (productJson?.title) {
            enriched.dt = productJson.title;
          }
          if (enriched.epi) {
            imageUrl = this.getVariantImageUrl(productJson, enriched.epi);
          }
        }

        if (!imageUrl) {
          imageUrl = this.getProductImageUrl(enriched);
        }

        if (!imageUrl && searchData?.image) {
          imageUrl = searchData.image;
        }

        enriched.iu = imageUrl;

        if (handle) {
          enriched.colorSwatchesHtml = await this.fetchColorSwatchesHtml(handle, enriched.epi);

          if (!productJson) {
            productJson = await this.fetchProductJson(handle);
          }

          if (productJson?.title) {
            enriched.dt = productJson.title;
          }

          const variant = this.getVariantFromProductJson(productJson, enriched.epi);

          if (variant?.price != null) {
            enriched.priceCents = variant.price;
          }

          enriched.isAvailable = this.getVariantAvailabilityFromProduct(productJson, enriched.epi);
        } else if (searchData?.title) {
          enriched.dt = searchData.title;
          enriched.colorSwatchesHtml = '';
          enriched.isAvailable = true;
        } else {
          enriched.colorSwatchesHtml = '';
          enriched.isAvailable = true;
        }

        if (enriched.dt) {
          enriched.dt = this.cleanProductTitle(enriched.dt);
        }

        enriched.availableFlag = enriched.isAvailable ? 'true' : 'false';

        if (enriched.epi && enriched.du) {
          enriched.du = this.ensureVariantProductUrl(enriched.du, enriched.epi);
        }

        return enriched;
      })
    );
  }

  async resolveProductHandle(card, button, productId) {
    let handle = this.getProductHandleFromCard(card, button);

    if (handle || !productId) {
      return handle;
    }

    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(
        `${root}search/suggest.json?q=${encodeURIComponent(productId)}&resources[type]=product&resources[limit]=10`,
        { credentials: 'same-origin' }
      );

      if (!response.ok) {
        return handle;
      }

      const data = await response.json();
      const results = data?.resources?.results?.products || [];
      const match = results.find((entry) => String(entry.id) === String(productId));

      return match?.handle || handle;
    } catch (error) {
      return handle;
    }
  }

  async onAddToCartClick(e) {
    e.preventDefault();

    const button = e.currentTarget;
    const variantId = button.getAttribute('data-variant-id');
    const productId = button.getAttribute('data-product-id');
    const card = button.closest('.swym-wishlist-card');

    if (!variantId || button.getAttribute('aria-disabled') === 'true' || button.dataset.processingAdd === 'true') {
      return;
    }

    button.dataset.processingAdd = 'true';

    try {
      if (typeof routes === 'undefined' || typeof fetchConfig !== 'function') {
        return;
      }

      const product = await this.resolveWishlistProduct(card, button, productId);

      if (product && this.needsSizeSelection(product)) {
        if (this.isWishlistQuickAddOverlayOpen(card)) {
          const selectedVariantId = this.getSelectedWishlistOverlayVariantId(card);

          if (selectedVariantId) {
            this.addVariantToCart(button, selectedVariantId, productId);
            return;
          }

          // Keep the selector open when no size is chosen yet (common on mobile
          // when the same ATC control is tapped again while the overlay is visible).
          return;
        }

        const sizes = this.getAvailableSizes(product, variantId);

        if (sizes.length) {
          this.openWishlistQuickAddOverlay(card, button, product, variantId, productId);
          return;
        }

        // Multi-size product but size list empty after filtering — still open overlay
        // with all product sizes rather than silently adding the stored epi.
        if (product.variants?.length > 1) {
          this.openWishlistQuickAddOverlay(card, button, product, variantId, productId);
          return;
        }
      }

      this.addVariantToCart(button, variantId, productId);
    } finally {
      delete button.dataset.processingAdd;
    }
  }

  addVariantToCart(button, variantId, productId) {
    const self = this;
    const card = button.closest('.swym-wishlist-card');
    const wishlistVariantId =
      card?.dataset.variantId || button.getAttribute('data-variant-id');

    if (!variantId || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-loading')) {
      return;
    }

    if (typeof routes === 'undefined' || typeof fetchConfig !== 'function') {
      return;
    }

    SwymWishlist.setWishlistActionLoading(button, true);
    SwymWishlist.setWishlistCardLoading(card, true);
    this.closeWishlistQuickAddOverlays(card);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const cart = document.querySelector('cart-drawer');
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          return;
        }

        button.setAttribute('data-state-cart', 'swym-added');

        // 1. Immediately remove product card from Favourite tab DOM
        if (card) {
          card.remove();
        }

        const container = self.wishlistContainer || document.getElementById('wishlist-items-container');
        if (container && container.querySelectorAll('.swym-wishlist-card').length === 0) {
          const emptyMarkup = `
            <div class="swym-wishlist-grid">
              <div class="empty-wishlist">
                <p>You don't have any products in your wishlist yet.</p>
              </div>
            </div>`;
          container.innerHTML = emptyMarkup;
        }

        // 2. Remove product from Swym Wishlist storage (use stored wishlist variant, not selected size)
        self.removeProductFromWishlist(wishlistVariantId, productId, function () {
          self.invalidateWishlistRender();
          if (window._swat) {
            window.swymRenderWishlist(window._swat);
          }
        });

        // 3. Update wishlist count elements
        if (window._swat) {
          const currentCount = SwymWishlist.getPersistedWishlistCount(window._swat);
          const newCount = Math.max(0, currentCount - 1);
          if (!self.wishlistCountElements) {
            self.wishlistCountElements = document.querySelectorAll('.js-wishlist-count');
          }
          self.wishlistCountElements.forEach((el) => {
            el.textContent = newCount;
          });
        }

        // 4. Automatically switch to Cart tab and render updated contents
        if (cart) {
          cart.dataset.activeTab = '#cart-products';
          cart.classList.remove('is-empty');
          cart.renderContents(response);
        }

        if (typeof window.activateCartDrawerTab === 'function') {
          window.activateCartDrawerTab('#cart-products', { animate: true });
        }

        // 5. Publish pub/sub cartUpdate event
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'swym-wishlist',
            productVariantId: variantId,
            cartData: response,
          });
        }

        // 6. Initialize Wishlist action buttons in Cart tab
        setTimeout(() => {
          if (typeof window.initCartDrawerWishlistButtons === 'function') {
            window.initCartDrawerWishlistButtons();
          }
        }, 100);
      })
      .catch(() => {})
      .finally(() => {
        SwymWishlist.setWishlistActionLoading(button, false);
        SwymWishlist.setWishlistCardLoading(card, false);
      });
  }

  removeProductFromWishlist(variantId, productId, callback) {
    if (!window._swat || typeof window._swat.fetch !== 'function') {
      if (typeof callback === 'function') callback();
      return;
    }

    const epi = variantId ? +variantId : null;
    const empi = productId ? +productId : null;
    const self = this;

    window._swat.fetch(function (products) {
      if (!Array.isArray(products) || products.length === 0) {
        if (typeof callback === 'function') callback();
        return;
      }

      const matchingProducts = products.filter(function (product) {
        if (empi && String(product.empi) === String(empi)) {
          return true;
        }

        if (epi && String(product.epi) === String(epi)) {
          return true;
        }

        return false;
      });

      if (matchingProducts.length === 0) {
        if (typeof callback === 'function') callback();
        return;
      }

      let remaining = matchingProducts.length;
      matchingProducts.forEach(function (product) {
        window._swat.removeFromWishList(product, function () {
          remaining -= 1;
          if (remaining <= 0) {
            self.invalidateWishlistRender();
            if (typeof callback === 'function') callback();
          }
        });
      });
    });
  }

  onRemoveBtnClick(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const card = button.closest('.swym-wishlist-card');
    const epi = button.getAttribute('data-variant-id');
    const empi = button.getAttribute('data-product-id');

    if (button.classList.contains('is-loading')) {
      return;
    }

    SwymWishlist.setWishlistActionLoading(button, true);
    SwymWishlist.setWishlistCardLoading(card, true);

    this.removeProductFromWishlist(epi, empi, function () {
      if (window._swat) {
        window.swymRenderWishlist(window._swat);
      }
    });
  }

  ensureWishlistLoader() {
    if (!this.wishlistContainer) {
      return;
    }

    if (!this._wishlistLoaderEl) {
      this._wishlistLoaderEl = document.createElement('div');
      this._wishlistLoaderEl.className = 'cart-drawer-wishlist-loader hidden';
      this._wishlistLoaderEl.setAttribute('aria-hidden', 'true');
      this._wishlistLoaderEl.innerHTML = WISHLIST_SPINNER_SVG;
    }

    if (!this.wishlistContainer.contains(this._wishlistLoaderEl)) {
      this.wishlistContainer.appendChild(this._wishlistLoaderEl);
    }
  }

  setWishlistContainerLoading(isLoading) {
    if (!this.wishlistContainer) {
      return;
    }

    this.ensureWishlistLoader();
    this.wishlistContainer.classList.toggle('cart-drawer-wishlist-container--loading', isLoading);
    this.wishlistContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    this._wishlistLoaderEl?.classList.toggle('hidden', !isLoading);
  }

  invalidateWishlistRender() {
    this._lastRenderKey = null;
    this._lastMarkup = null;
    this._swatchHtmlCache.clear();
    this._productJsonCache?.clear();

    if (this.wishlistContainer) {
      this.wishlistContainer.classList.remove('wishlist-rendered');
    }
  }

  getWishlistRenderKey(products) {
    if (!products?.length) {
      return 'empty';
    }

    const currencySymbol = this.getStorefrontCurrencySymbol();

    return `${currencySymbol}|${products
      .map((product) => {
        const availableFlag = product.availableFlag || (product.isAvailable === false ? 'false' : 'true');
        return `${product.empi}:${product.epi}:${availableFlag}`;
      })
      .join('|')}`;
  }

  renderWishlist(swat) {
    if (!swat || typeof swat.fetch !== 'function') {
      return;
    }

    this._renderId = (this._renderId || 0) + 1;
    const renderId = this._renderId;

    if (!this.wishlistContainer) {
      this.wishlistContainer = document.getElementById('wishlist-items-container');
    }

    this.setWishlistContainerLoading(true);

    swat.fetch(async (products) => {
      try {
      if (renderId !== this._renderId) {
        return;
      }

      if (!this.wishlistContainer) {
        this.wishlistContainer = document.getElementById('wishlist-items-container');
      }

      if (!this.wishlistContainer) {
        return;
      }

      const count = products ? products.length : 0;

      if (!this.wishlistCountElements) {
        this.wishlistCountElements = document.querySelectorAll('.js-wishlist-count');
      }

      this.wishlistCountElements.forEach((el) => {
        el.textContent = count;
      });

      if (!products || products.length === 0) {
        const emptyMarkup = `
          <div class="swym-wishlist-grid">
            <div class="empty-wishlist">
              <p>You don't have any products in your wishlist yet.</p>
            </div>
          </div>`;

        if (this._lastRenderKey !== 'empty') {
          this.wishlistContainer.innerHTML = emptyMarkup;
          this._lastRenderKey = 'empty';
          this._lastMarkup = emptyMarkup;
        }

          this.wishlistContainer.classList.add('wishlist-rendered');
          this.ensureWishlistLoader();
        return;
      }

      const enrichedProducts = await this.enrichProductsFromShopify(products);

      if (renderId !== this._renderId) {
        return;
      }

      const renderKey = this.getWishlistRenderKey(enrichedProducts);

      if (renderKey === this._lastRenderKey && this.wishlistContainer.classList.contains('wishlist-rendered')) {
        return;
      }

      const handleCounts = {};

      enrichedProducts.forEach((product) => {
        const handle = product.productHandle || this.getHandleFromUrl(product.du) || '';

        if (handle) {
          handleCounts[handle] = (handleCounts[handle] || 0) + 1;
        }
      });

      const formattedWishlistedProducts = enrichedProducts.map((p) => {
        p.formattedPrice = this.formatStorefrontPrice(this.resolveWishlistPriceCents(p));
        p.iu = this.getProductImageUrl(p);
        p.isInCart = _swat.platform.isInDeviceCart(p.epi) || p.et == _swat.EventTypes.addToCart;
        p.addToCartLabel = this.addToCartLabel;
        p.removeLabel = this.removeLabel;
        p.notifyMeLabel = this.notifyMeLabel;
        if (typeof p.isAvailable !== 'boolean') {
          p.isAvailable = true;
        }
        p.availableFlag = p.isAvailable ? 'true' : 'false';
        p.showAddToCart = p.isAvailable;
        p.showNotifySlot = !p.isAvailable;
        if (!p.productHandle) {
          p.productHandle = this.getHandleFromUrl(p.du) || '';
        }
        if (p.productHandle && handleCounts[p.productHandle] > 1) {
          p.restockButtonId = `restock-${p.productHandle}-${p.epi}`;
        } else if (p.productHandle) {
          p.restockButtonId = `restock-${p.productHandle}`;
        } else {
          p.restockButtonId = `restock-wishlist-${p.epi}`;
        }
        if (p.epi && p.du) {
          p.du = this.ensureVariantProductUrl(p.du, p.epi);
        }
        if (!p.colorSwatchesHtml) {
          p.colorSwatchesHtml = '';
        }

        return p;
      });

      const productCardsMarkup = SwymUtils.renderTemplateString(this.productCardMarkup, {
        products: formattedWishlistedProducts,
      });

      if (renderId !== this._renderId || productCardsMarkup === this._lastMarkup) {
        this.wishlistContainer.classList.add('wishlist-rendered');
        return;
      }

      const scrollTop = this.wishlistContainer.scrollTop;
      this._lastRenderKey = renderKey;
      this._lastMarkup = productCardsMarkup;
      this.closeWishlistQuickAddOverlays();
      this.wishlistContainer.innerHTML = productCardsMarkup;
      this.wishlistContainer.scrollTop = scrollTop;
      this.wishlistContainer.classList.add('wishlist-rendered');
      this.ensureWishlistLoader();
      this.setupNotifyButtonDedupe();
      this.syncWishlistCardActions();
      this.debouncedDedupeNotifyButtons?.();
      this.bindWishlistQuickAddSubmit();
      this.initWishlistSizeSelectors();
      document.dispatchEvent(new Event('wishlist:rendered'));
      this.handleWishlistNotifyInit();
      } finally {
        if (renderId === this._renderId) {
          this.setWishlistContainerLoading(false);
        }
      }
    });
  }
}

function resolvePdpSelectedVariantId(wishlistButton) {
  const variantInput = document.querySelector(
    '.main_product_section form[data-type="add-to-cart-form"] input[name="id"]'
  );

  if (variantInput?.value) {
    return variantInput.value;
  }

  return wishlistButton?.getAttribute('data-variant-id') || '';
}

function buildPdpWishlistProductData(button) {
  const productId = button.getAttribute('data-product-id');
  const variantId = resolvePdpSelectedVariantId(button);
  const productTitle = button.getAttribute('data-product-title') || '';
  const productImage = button.getAttribute('data-product-image') || '';
  const productUrl = button.getAttribute('data-product-url') || window.location.href;
  const wishlistElement = document.querySelector('swym-wishlist');
  const normalizedUrl =
    wishlistElement && typeof wishlistElement.ensureVariantProductUrl === 'function'
      ? wishlistElement.ensureVariantProductUrl(productUrl, variantId)
      : productUrl;
  const priceAttr = parseFloat(button.getAttribute('data-product-price') || '');

  return {
    empi: productId ? parseInt(productId, 10) : undefined,
    epi: variantId ? parseInt(variantId, 10) : undefined,
    du: normalizedUrl || window.location.href,
    iu: productImage,
    dt: productTitle,
    pr: Number.isFinite(priceAttr) ? priceAttr : 0,
    et: 4,
  };
}

function savePdpVariantToWishlist(button) {
  const productData = buildPdpWishlistProductData(button);

  if (!productData.epi) {
    delete button.dataset.processingWishlist;
    return;
  }

  const onSuccess = function () {
    button.classList.add('swym-added');
    delete button.dataset.processingWishlist;

    const wishlistElement = document.querySelector('swym-wishlist');

    if (wishlistElement && typeof wishlistElement.invalidateWishlistRender === 'function') {
      wishlistElement.invalidateWishlistRender();
    }

    if (window._swat) {
      const upi = window._swat.persistedData?.upi;
      const count = Array.isArray(upi) ? upi.length : 0;

      document.querySelectorAll('.js-wishlist-count').forEach((el) => {
        el.textContent = count;
      });
    }
  };

  const onError = function () {
    delete button.dataset.processingWishlist;
  };

  const saveToSwym = function (swat) {
    if (!swat || typeof swat.addToWishList !== 'function') {
      onError();
      return;
    }

    swat.addToWishList(productData, onSuccess, onError);
  };

  if (window._swat) {
    saveToSwym(window._swat);
  } else if (window.SwymCallbacks && typeof window.SwymCallbacks.push === 'function') {
    window.SwymCallbacks.push(saveToSwym);
  } else {
    onError();
  }
}

function handlePdpWishlistClick(event) {
  const wishlistButton = event.target.closest('.main_product_section .swym-add-to-wishlist-view-product');

  if (!wishlistButton) {
    return;
  }

  if (wishlistButton.classList.contains('swym-added')) {
    return;
  }

  const variantId = resolvePdpSelectedVariantId(wishlistButton);

  if (!variantId) {
    return;
  }

  if (wishlistButton.dataset.processingWishlist === 'true') {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  wishlistButton.dataset.processingWishlist = 'true';
  savePdpVariantToWishlist(wishlistButton);
}

document.addEventListener('click', handlePdpWishlistClick, true);

// Register the custom element
customElements.define('swym-wishlist', SwymWishlist);

let swymRenderWishlistTimer;

window.swymRenderWishlist = function (swat, options) {
  clearTimeout(swymRenderWishlistTimer);

  const delay = options && options.immediate ? 0 : 150;

  swymRenderWishlistTimer = setTimeout(function () {
  const wishlistElement = document.querySelector('swym-wishlist');
  if (wishlistElement) {
    wishlistElement.renderWishlist(swat);
    }
  }, delay);
};

window.initCartDrawerWishlistButtons = function () {
  if (window._swat && typeof window._swat.initializeActionButtons === 'function') {
    window._swat.initializeActionButtons('#cart-products');
  }
};

function setupWishlistDrawerOnAdd(swat) {
  if (!swat?.evtLayer) {
    return;
  }

  const handleWishlistChange = function (event, isAdd) {
    const wishlistElement = document.querySelector('swym-wishlist');

    if (wishlistElement) {
      wishlistElement.invalidateWishlistRender();
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const isMovingFromCart =
      cartDrawer &&
      (cartDrawer.dataset.movingToWishlist === 'true' || cartDrawer.dataset.pendingWishlistSwitch === 'true');

    // If moving from cart inside the drawer, do NOT auto-switch tabs here!
    // The tab will switch smoothly once cart removal and drawer content update complete.
    if (isMovingFromCart) {
      return;
    }

    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const isInsideCartDrawer = trigger && trigger.closest('cart-drawer');

    // Only auto-open/switch to Wishlist tab if the trigger was OUTSIDE the Cart Drawer (e.g. collection/PDP)
    if (isAdd && !isInsideCartDrawer && typeof window.openCartDrawerWishlistTab === 'function') {
      window.openCartDrawerWishlistTab({
        trigger: trigger instanceof HTMLElement ? trigger : null,
        animate: true,
        immediate: true,
      });
      return;
    }

    if (window._swat) {
      window.swymRenderWishlist(window._swat, { immediate: true });
    }
  };

  swat.evtLayer.addEventListener('sw:addedtowishlist', function (event) {
    handleWishlistChange(event, true);
  });

  swat.evtLayer.addEventListener('sw:removedfromwishlist', function (event) {
    handleWishlistChange(event, false);
  });
}

if (!window.SwymCallbacks) {
  window.SwymCallbacks = [];
}
window.SwymCallbacks.push(window.swymRenderWishlist);
window.SwymCallbacks.push(setupWishlistDrawerOnAdd);
window.SwymCallbacks.push(function () {
  window.initCartDrawerWishlistButtons();
});

function clearWishlistMoveFlags(cartDrawer, button, options) {
  options = options || {};
  if (cartDrawer) {
    if (cartDrawer._wishlistMoveSafetyTimer) {
      clearTimeout(cartDrawer._wishlistMoveSafetyTimer);
      delete cartDrawer._wishlistMoveSafetyTimer;
    }
    if (options.resetTab) {
      cartDrawer.dataset.activeTab = '#cart-products';
    }
    delete cartDrawer.dataset.pendingWishlistSwitch;
    delete cartDrawer.dataset.movingToWishlist;
  }
  if (button) {
    button.dataset.processing = 'false';
  }
}

function removeCartLineForWishlistMove(button, lineIndex) {
  const cartDrawerItems =
    button.closest('cart-drawer-items') ||
    document.querySelector('cart-drawer-items') ||
    button.closest('cart-items') ||
    document.querySelector('cart-items');

  if (cartDrawerItems && lineIndex && typeof cartDrawerItems.updateQuantity === 'function') {
    cartDrawerItems.updateQuantity(lineIndex, 0, { currentTarget: button });
    return;
  }

  const row = button.closest('tr') || button.closest('.cart-item');
  const removeBtn = row?.querySelector('cart-remove-button button, cart-remove-button');
  if (removeBtn) {
    removeBtn.click();
  }
}

function handleMoveFromCartToWishlist(e) {
  const button = e.target.closest('.cart-item__save-btn');
  if (!button) return;

  if (button.dataset.processing === 'true') {
    e.preventDefault();
    e.stopImmediatePropagation();
    return;
  }
  button.dataset.processing = 'true';

  e.preventDefault();
  e.stopImmediatePropagation();

  button.classList.add('swym-added');

  const variantId = button.getAttribute('data-variant-id');
  const productId = button.getAttribute('data-product-id');
  const productUrl = button.getAttribute('data-product-url');
  const productImage = button.getAttribute('data-product-image');
  const productTitle = button.getAttribute('data-product-title') || '';
  const productPrice = button.getAttribute('data-product-price') || 0;
  const lineIndex = button.getAttribute('data-line-index');

  const cartDrawer = button.closest('cart-drawer') || document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.dataset.pendingWishlistSwitch = 'true';
    cartDrawer.dataset.movingToWishlist = 'true';
    cartDrawer.dataset.activeTab = '#cart-wishlist';
  }

  const safetyTimeout = setTimeout(() => {
    if (cartDrawer && cartDrawer.dataset.pendingWishlistSwitch === 'true') {
      clearWishlistMoveFlags(cartDrawer, button, { resetTab: true });
    }
  }, 8000);
  if (cartDrawer) {
    cartDrawer._wishlistMoveSafetyTimer = safetyTimeout;
  }

  const productData = {
    empi: productId ? parseInt(productId, 10) : undefined,
    epi: variantId ? parseInt(variantId, 10) : undefined,
    du: productUrl || window.location.href,
    iu: productImage || '',
    dt: productTitle,
    pr: productPrice ? parseFloat(productPrice) : 0,
    et: 4,
  };

  const abortMove = function () {
    button.classList.remove('swym-added');
    clearWishlistMoveFlags(cartDrawer, button, { resetTab: true });
  };

  const onSwymAddSuccess = function () {
    const wishlistElement = document.querySelector('swym-wishlist');
    if (wishlistElement) {
      wishlistElement.invalidateWishlistRender();
    }

    if (window._swat) {
      const currentCount = SwymWishlist.getPersistedWishlistCount(window._swat);
      document.querySelectorAll('.js-wishlist-count').forEach((el) => {
        el.textContent = currentCount;
      });
    }

    removeCartLineForWishlistMove(button, lineIndex);
  };

  const saveToSwym = function (swat) {
    if (!swat || typeof swat.addToWishList !== 'function') {
      abortMove();
      return;
    }

    swat.addToWishList(productData, onSwymAddSuccess, abortMove);
  };

  if (window._swat) {
    saveToSwym(window._swat);
  } else if (window.SwymCallbacks && typeof window.SwymCallbacks.push === 'function') {
    window.SwymCallbacks.push(saveToSwym);
  } else {
    abortMove();
  }
}

document.addEventListener('click', handleMoveFromCartToWishlist, true);

document.addEventListener('DOMContentLoaded', function () {
  window.initCartDrawerWishlistButtons();

  document.addEventListener('cart:refresh', function () {
    if (window._swat) {
      setTimeout(() => {
        window.swymRenderWishlist(window._swat);
      }, 300);
    }
    window.initCartDrawerWishlistButtons();
  });

  document.addEventListener('cart-drawer:open', function () {
    const cartDrawer = document.querySelector('cart-drawer');
    const activeTab = cartDrawer?.dataset.activeTab;
    const wishlistElement = document.querySelector('swym-wishlist');

    wishlistElement?.restoreWishlistOverlaysFromBody?.();

    if (activeTab === '#cart-wishlist' && window._swat) {
      window.swymRenderWishlist(window._swat);
    } else {
      window.initCartDrawerWishlistButtons();
    }

    wishlistElement?.initWishlistSizeSelectors?.();
    SwymWishlist.setupRecommendationNotifyDedupe();
    SwymWishlist.initCartDrawerRecommendationNotify();
  });

  document.addEventListener('cart-drawer:updated', function () {
    window.initCartDrawerWishlistButtons();
    document.querySelector('swym-wishlist')?.initWishlistSizeSelectors?.();
    SwymWishlist.setupRecommendationNotifyDedupe();
    SwymWishlist.initCartDrawerRecommendationNotify();
  });

  document.addEventListener('shopify:section:load', function () {
    if (window._swat) {
      setTimeout(() => {
        window.swymRenderWishlist(window._swat);
      }, 300);
    }
    window.initCartDrawerWishlistButtons();
  });
});
